// src/components/Layout.jsx

import React, {
  useState,
  useLayoutEffect,
  useEffect,
  cloneElement,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiShoppingCart } from "react-icons/fi";
import Sidebar from "./Sidebar";
import "./Layout.css";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const Layout = ({ children }) => {
  const { currentUser } = useAuth();
  const { cartCount } = useCart();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [topPadding, setTopPadding] = useState(60);
  const [isScreenReady, setIsScreenReady] = useState(false);

  const [chatList, setChatList] = useState([]);
  const [visionList, setVisionList] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const [activeChat, setActiveChat] = useState(null);
  const [activeVision, setActiveVision] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const pathnameLower = (location?.pathname || "").toLowerCase();

  const isShoppingRoute =
    pathnameLower === "/billing" ||
    pathnameLower === "/cart" ||
    pathnameLower === "/checkout";

  const shouldShowMobileCart = Boolean(
    currentUser &&
      cartCount > 0 &&
      isShoppingRoute
  );

  useLayoutEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      const mobile = width < 1024;
      const tablet = false;

      setIsMobile(mobile);
      setIsTablet(tablet);
      setIsSidebarOpen(!mobile);

      const topBar = document.getElementById("top-bar");
      const barHeight = topBar ? topBar.offsetHeight : 60;

      setTopPadding(mobile ? barHeight : 40);
      setIsScreenReady(true);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isMobile || isTablet) {
      html.style.overflow = isSidebarOpen ? "hidden" : "auto";
      body.style.overflow = isSidebarOpen ? "hidden" : "auto";
    } else {
      html.style.overflow = "auto";
      body.style.overflow = "auto";
    }

    return () => {
      html.style.overflow = "auto";
      body.style.overflow = "auto";
    };
  }, [isMobile, isTablet, isSidebarOpen]);

  useEffect(() => {
    const pathname = (location?.pathname || "").toLowerCase();
    const isHomeRoute = pathname === "/" || pathname === "/home";

    try {
      if (isHomeRoute) document.body.classList.add("home-route");
      else document.body.classList.remove("home-route");
    } catch {}

    return () => {
      try {
        document.body.classList.remove("home-route");
      } catch {}
    };
  }, [location]);

  const isAuthRoute =
    pathnameLower === "/login" ||
    pathnameLower === "/signup" ||
    pathnameLower === "/check-your-email" ||
    pathnameLower === "/complete-signup" ||
    pathnameLower === "/verify-otp" ||
    pathnameLower === "/forgot-password";

  const getIdToken = useCallback(async () => {
    if (!currentUser) return "";

    try {
      return await currentUser.getIdToken();
    } catch {
      return "";
    }
  }, [currentUser]);

  const fetchChatList = useCallback(async () => {
    try {
      if (!HOST) return;

      const token = await getIdToken();

      if (!token) {
        setChatList([]);
        return;
      }

      const res = await fetch(`${HOST}/chat/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ search_term: "" }),
      });

      const data = await res.json();

      if (data.success) {
        setChatList(data.results || []);
      } else {
        setChatList([]);
      }
    } catch (err) {
      setChatList([]);
    }
  }, [getIdToken]);

  const fetchVisionList = useCallback(async () => {
    try {
      if (!HOST) return;

      const token = await getIdToken();

      if (!token) {
        setVisionList([]);
        return;
      }

      const res = await fetch(`${HOST}/vision/sessions/list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          search_term: "",
          include_archived: false,
          include_deleted: false,
          limit: 50,
          order: "desc",
          include_preview: false,
          include_latest_thumb: false,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setVisionList(data.results || []);
      } else {
        setVisionList([]);
      }
    } catch (err) {
      setVisionList([]);
    }
  }, [getIdToken]);

  useEffect(() => {
    if (isAuthRoute) {
      setActiveChat(null);
      setActiveVision(null);
      setChatList([]);
      setVisionList([]);
      return;
    }

    if (!currentUser) {
      setActiveChat(null);
      setActiveVision(null);
      setChatList([]);
      setVisionList([]);
      return;
    }

    fetchChatList();
    fetchVisionList();
  }, [currentUser, isAuthRoute, fetchChatList, fetchVisionList]);

  const handleNewChat = useCallback(() => {
    setActiveChat(null);
    setActiveVision(null);

    try {
      navigate("/dashboard");
    } catch {}

    fetchChatList();
    fetchVisionList();
  }, [navigate, fetchChatList, fetchVisionList]);

  const handleFirstMessageSent = () => {
    fetchChatList();
    fetchVisionList();
  };

  useEffect(() => {
    const listener = () => {
      if (isAuthRoute) return;
      if (!currentUser) return;

      fetchChatList();
      fetchVisionList();
    };

    window.addEventListener("triggerRefreshSidebar", listener);

    return () => window.removeEventListener("triggerRefreshSidebar", listener);
  }, [fetchChatList, fetchVisionList, currentUser, isAuthRoute]);

  if (!isScreenReady) return null;

  let contentMarginLeft = isMobile || isTablet ? 0 : isCollapsed ? 60 : 220;

  const mainStyle = {
    marginLeft: `${contentMarginLeft}px`,
    paddingTop: `${topPadding}px`,
  };

  const childOut = React.isValidElement(children)
    ? cloneElement(children, {
        chatList,
        visionList,
        showSearchModal,
        setShowSearchModal,
        activeChat,
        setActiveChat,
        activeVision,
        setActiveVision,
        handleNewChat,
        onFirstMessageSent: handleFirstMessageSent,
      })
    : children;

  return (
    <div className="layout-container">
      {(isMobile || isTablet) && (
        <div id="top-bar" style={styles.topBar}>
          <button
            type="button"
            onClick={() => setIsSidebarOpen((prev) => !prev)}
            style={styles.hamburger}
            aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          >
            {isSidebarOpen ? "✖" : "☰"}
          </button>

          {shouldShowMobileCart ? (
            <button
              type="button"
              onClick={() => navigate("/cart")}
              style={styles.mobileCartButton}
              aria-label={`Open cart with ${cartCount} ${cartCount === 1 ? "item" : "items"}`}
            >
              <FiShoppingCart style={styles.mobileCartIcon} />
              <span style={styles.mobileCartBadge}>{cartCount}</span>
            </button>
          ) : (
            <div style={styles.mobileTopBarSpacer} />
          )}
        </div>
      )}

      <Sidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        isMobile={isMobile}
        isTablet={isTablet}
        chatList={chatList}
        visionList={visionList}
        setShowSearchModal={setShowSearchModal}
        activeChat={activeChat}
        setActiveChat={setActiveChat}
        activeVision={activeVision}
        setActiveVision={setActiveVision}
        handleNewChat={handleNewChat}
      />

      <main className="main-content" style={mainStyle}>
        {childOut}
      </main>
    </div>
  );
};

const styles = {
  topBar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "60px",
    background: "#1E2A78",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: "15px",
    paddingRight: "15px",
    paddingTop: "env(safe-area-inset-top, 0px)",
    boxSizing: "border-box",
    zIndex: 1000,
  },

  hamburger: {
    width: "42px",
    height: "42px",
    fontSize: "24px",
    color: "#fff",
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
  },

  mobileTopBarSpacer: {
    width: "42px",
    height: "42px",
    flex: "0 0 42px",
  },

  mobileCartButton: {
    position: "relative",
    width: "42px",
    height: "42px",
    flex: "0 0 42px",
    marginRight: "0px",
    border: "1px solid rgba(255,255,255,0.28)",
    background: "rgba(255,255,255,0.12)",
    color: "#FFFFFF",
    borderRadius: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
  },

  mobileCartIcon: {
    fontSize: "21px",
  },

  mobileCartBadge: {
    position: "absolute",
    top: "-4px",
    right: "-2px",
    minWidth: "20px",
    height: "20px",
    padding: "0 5px",
    borderRadius: "999px",
    background: "#FFFFFF",
    color: "#17245F",
    border: "1px solid rgba(23,36,95,0.18)",
    fontSize: "12px",
    fontWeight: 900,
    lineHeight: "20px",
    textAlign: "center",
    boxSizing: "border-box",
  },
};

export default Layout;
